import React, { useEffect, useState, useRef } from 'react'
import { Header } from '../../../header/Header'
import { OrganizationAdminHeader } from '../OrganizationAdminHeader'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getUserOrganizations } from '../../../../store/user_organizations'
import { FormOrganizationDetails } from '../../../admin/organizations/details/FormOrganizationDetails'
import { useForm } from '../../../hooks/useForm'
import axios from 'axios'
import Swal from 'sweetalert2'
import { zynity_api } from '../../../../config/external-routes'
import { Loading } from '../../../ui/loading/Loading'
import { ImageUploader } from '../../../ui/uploader/image/ImageUploader'
import { publicUrl } from '../../../../helpers/files'
import { useDebounceEffect } from '../../../ui/uploader/image/useDebounceEffect'

// use the same gradient helper as the new homepage
import { gradientFor } from '../../../user_home/bodyV2/accordion/gradientFor'
import {
  ORG_STATEMENT_HIDE_FIELD,
  parseOrgMainTraxStatementOrder,
} from '../../../../helpers/mainTraxOrgStatements'

export const OrganizationProfile = () => {
  const { organization_id } = useParams()
  const dispatch = useDispatch()
  const authenticated_user = useSelector((state: any) => state.auth)
  const user_organizations_obj = useSelector((state: any) => state.user_organizations)
  const user_organizations = user_organizations_obj.data?.data || []
  const organization = user_organizations.find((item: any) => item.id == organization_id)
  const [data, setFormDatum, setData] = useForm(organization || {})

  const [tempOrgImg, setTempOrgImg] = useState<string | null>(null)
  const justSavedRef = useRef(false)
  const initialDataRef = useRef<any>(null)
  const isInitialLoadRef = useRef(true)
  const autoSaveEnabledRef = useRef(false) // Disabled by default to prevent reload loops

  // Function to clean and prepare data for submission
  const prepareSubmitData = (formData: any) => {
    const submitData = { ...formData }
    
    // Handle date field - ensure it's in correct format or exclude if invalid
    if (submitData.date) {
      const dateValue = submitData.date
      if (typeof dateValue === 'string' && dateValue.includes(' ')) {
        // Check if it's a valid datetime format (YYYY-MM-DD HH:MM:SS)
        const dateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
        if (!dateRegex.test(dateValue)) {
          console.warn('Invalid date format detected, excluding date field from submission:', dateValue)
          delete submitData.date
        }
      } else if (dateValue && !(dateValue instanceof Date) && typeof dateValue !== 'string') {
        console.warn('Date field has unexpected type, excluding from submission:', typeof dateValue)
        delete submitData.date
      }
    }
    
    // Exclude read-only or system fields that shouldn't be updated
    const fieldsToExclude = ['idAuthor', 'id']
    fieldsToExclude.forEach(field => {
      if (submitData[field] !== undefined) {
        delete submitData[field]
      }
    })
    
    return submitData
  }

  // Auto-save function (called on field changes)
  const autoSave = async () => {
    // Don't auto-save if disabled, on initial load, or if we just manually saved
    if (!autoSaveEnabledRef.current || isInitialLoadRef.current || justSavedRef.current) {
      return
    }

    // Check if data has actually changed
    if (!initialDataRef.current || JSON.stringify(data) === JSON.stringify(initialDataRef.current)) {
      return
    }

    if (!authenticated_user?.access_token) {
      return
    }

    const submitData = prepareSubmitData(data)

    try {
      // Disable auto-save during the save operation to prevent loops
      autoSaveEnabledRef.current = false
      
      axios.defaults.headers.common = { Authorization: `Bearer ${authenticated_user.access_token}` }
      const response = await axios.put(`${zynity_api.admin_organizations}/${organization_id}`, submitData, {
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' }
      })

      justSavedRef.current = true
      
      // Update initial data ref to current data after successful save (without triggering setData)
      // This prevents the loop - we only update the ref, not the state
      if (response.data?.organization) {
        initialDataRef.current = { ...data, ...response.data.organization }
      } else if (response.data?.data) {
        initialDataRef.current = { ...data, ...response.data.data }
      } else {
        initialDataRef.current = { ...data }
      }
      
      // Reset flags after a delay
      setTimeout(() => {
        justSavedRef.current = false
        autoSaveEnabledRef.current = true
      }, 1000)

      // Refresh Redux store (but don't update form state - let Redux handle it naturally)
      dispatch(getUserOrganizations())
    } catch (error: any) {
      console.error('❌ Auto-save failed:', error)
      // Re-enable auto-save on error
      autoSaveEnabledRef.current = true
      // Don't show error toast for auto-save failures to avoid annoying the user
      // The manual save button will still work
    }
  }

  // Auto-save on data changes (debounced by 2 seconds)
  // Only trigger if data actually changed and we're not in a save operation
  useDebounceEffect(() => {
    if (autoSaveEnabledRef.current && !isInitialLoadRef.current && !justSavedRef.current) {
      autoSave()
    }
  }, 2000, [data])

  // ---------- SAVE main form ----------
  const handleSubmitForm = (event: any) => {
    event.preventDefault()

    const form = document.getElementById('dataFormDetails') as HTMLFormElement | null
    if (!form || !form.checkValidity()) {
      form && form.classList.add('was-validated')
      return false
    }


    if (!data) {
      console.error('Form data is undefined or null')
      Swal.fire({ icon: 'error', title: 'Error', text: 'Form data is missing. Please refresh the page and try again.' })
      return
    }

    if (!authenticated_user?.access_token) {
      console.error('Access token is missing')
      Swal.fire({ icon: 'error', title: 'Error', text: 'Authentication token is missing. Please log in again.' })
      return
    }

    // Temporarily disable auto-save during manual save
    autoSaveEnabledRef.current = false
    
    // Prepare data for submission using the shared function
    const submitData = prepareSubmitData(data)
    axios.defaults.headers.common = { Authorization: `Bearer ${authenticated_user.access_token}` }
    axios
      .put(`${zynity_api.admin_organizations}/${organization_id}`, submitData, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        // Mark that we just saved to prevent useEffect from overwriting
        justSavedRef.current = true
        
        // After successful save, preserve the user's input data
        // The server response might have stale or different values, so we trust what the user just saved
        // Only merge in fields that weren't in the user's input (like server-computed fields)
        let serverFields = null
        if (response.data?.organization) {
          serverFields = response.data.organization
        } else if (response.data?.data) {
          serverFields = response.data.data
        }
        
        // Keep the user's input data (what they just saved successfully)
        // Only merge in fields from server that we didn't send (like id, timestamps, etc.)
        const fieldsWeSent = Object.keys(submitData)
        const mergedData = { ...data } // Start with user's input
        
        if (serverFields) {
          // Only merge fields from server that we didn't send (read-only/system fields)
          Object.keys(serverFields).forEach(key => {
            if (!fieldsWeSent.includes(key)) {
              // This is a server-only field, merge it in
              mergedData[key] = serverFields[key]
            }
            // For fields we sent, keep the user's value (data[key]) - don't overwrite with server
          })
        }
        
        // Update form with merged data (user input preserved)
        setData(mergedData)
        
        // Update initialDataRef to match what we just saved (user's input)
        initialDataRef.current = { ...mergedData }
        
        // Refresh organization data in Redux AFTER we've updated the form
        // Use a longer delay to ensure form update completes first
        setTimeout(() => {
          dispatch(getUserOrganizations())
          // Keep the flag true longer to prevent Redux from overwriting
          setTimeout(() => {
            justSavedRef.current = false
          }, 2000) // Keep protection for 2 more seconds after Redux refresh
        }, 100)
        
        // Re-enable auto-save after manual save completes
        autoSaveEnabledRef.current = true
        // Update initial data ref
        initialDataRef.current = mergedData
        
        Swal.fire({ icon: 'success', title: 'Success!', text: response.data.message })
      })
      .catch((error) => {
        // Re-enable auto-save even on error
        autoSaveEnabledRef.current = true
        console.error('❌ Error updating organization profile:', error)
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Error status:', error.response.status)
          console.error('Error response:', error.response)
          console.error('Error data:', error.response?.data)
          const errorMessage = error.response?.data?.message || error.response?.data?.error || `Server error: ${error.response.status}`
          Swal.fire({ icon: 'error', title: 'Oops...', text: errorMessage })
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received:', error.request)
          Swal.fire({ icon: 'error', title: 'Network Error', text: 'No response from server. Please check your connection and try again.' })
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error setting up request:', error.message)
          Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'We have problems updating the Organization!' })
        }
      })
      .finally(() => {
        // Request completed
      })
  }


  // Update form data when organization loads (but not if we just saved)
  useEffect(() => {
    // Skip update if we just saved (to prevent overwriting with stale Redux data)
    if (justSavedRef.current) {
      return
    }
    
    // Only update if organization exists and we have a valid ID match
    if (organization && organization.id == organization_id) {
      // Check if data is actually different before updating to prevent unnecessary re-renders
      const orgString = JSON.stringify(organization)
      const currentDataString = JSON.stringify(data)
      
      if (orgString !== currentDataString) {
        // Only update from Redux if we don't have unsaved changes
        // Compare current form data with initial data to detect unsaved changes
        const hasUnsavedChanges = initialDataRef.current && 
          JSON.stringify(data) !== JSON.stringify(initialDataRef.current)
        
        if (!hasUnsavedChanges) {
          setData(organization)
        }
      }
      
      // Initialize or update the initial data reference
      if (!initialDataRef.current || isInitialLoadRef.current) {
        initialDataRef.current = { ...organization }
        isInitialLoadRef.current = true
        // Mark initial load as complete after a short delay
        setTimeout(() => {
          isInitialLoadRef.current = false
        }, 1000)
      } else if (!justSavedRef.current) {
        // Only update initial data ref from Redux if we didn't just save
        // (to preserve the user's saved data)
        initialDataRef.current = { ...organization }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, organization_id])

  // keep image in form state
  useEffect(() => {
    if (tempOrgImg !== null) {
      setData({ ...data, image: tempOrgImg })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempOrgImg])

  // ---------- ORG HEADER COLOR ----------
  // Keep this list in sync with gradientFor()
  const THEME_KEYS = [
    'zynity',
    'ocean',
    'forest',
    'coral',
    'amber',
    'plum',
    'steel',
    'sky',
    'rose',
    'charcoal',
    'mint',
    'indigo',
    'lava'
  ]

  // Ensure we have an initial value so Save always includes it
  useEffect(() => {
    if (typeof data?.color_key === 'undefined') {
      setData({ ...data, color_key: organization?.color_key ?? null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Optimistic save for color selection
  const saveColorKey = async (key: string | null) => {
    const previous = data?.color_key ?? null
    setData({ ...data, color_key: key })

    try {
      axios.defaults.headers.common = { Authorization: `Bearer ${authenticated_user.access_token}` }
      await axios.put(`${zynity_api.admin_organizations}/${organization_id}`, { color_key: key })
      // (Optional) toast success here
    } catch (e) {
      // revert on error
      setData({ ...data, color_key: previous })
      Swal.fire({ icon: 'error', title: 'Could not save color', text: 'Please try again.' })
    }
  }

  const handlePickColor = (key: string) => {
    if ((data?.color_key ?? null) === key) return
    saveColorKey(key)
  }

  const handleClearColor = () => {
    if ((data?.color_key ?? null) === null) return
    saveColorKey(null)
  }

  // ---------- Form columns ----------
  const formParsedValues = {
    name: {
      value: data.name,
      type: 'text',
      name: 'name',
      human_name: 'Org Name',
      required: true,
      invalid_feedback: 'Please add the organization name'
    },
    url: { value: data.url, type: 'text', name: 'url', human_name: 'Web Address', required: false, invalid_feedback: null },
    contact: { value: data.contact, type: 'text', name: 'contact', human_name: 'Contact', required: false, invalid_feedback: null },
    email: { value: data.email, type: 'email', name: 'email', human_name: 'Email', required: false, invalid_feedback: null },
    address: { value: data.address, type: 'text', name: 'address', human_name: 'Address', required: false, invalid_feedback: null },
    address2: { value: data.address2, type: 'text', name: 'address2', human_name: 'Address 2', required: false, invalid_feedback: null },
    city: { value: data.city, type: 'text', name: 'city', human_name: 'City', required: false, invalid_feedback: null },
    state: { value: data.state, type: 'text', name: 'state', human_name: 'State', required: false, invalid_feedback: null },
    zip: { value: data.zip, type: 'text', name: 'zip', human_name: 'Zip', required: false, invalid_feedback: null },
    country: { value: data.country, type: 'text', name: 'country', human_name: 'Country', required: false, invalid_feedback: null },
    phone: { value: data.phone, type: 'tel', name: 'phone', human_name: 'Phone', required: false, invalid_feedback: null },
    phone2: { value: data.phone2, type: 'tel', name: 'phone2', human_name: 'Phone 2', required: false, invalid_feedback: null },
    purpose: { value: data.purpose, type: 'tinymce', name: 'purpose', human_name: 'Purpose', required: false, invalid_feedback: null },
    hide_purpose_in_main_trax: {
      value: data.hide_purpose_in_main_trax,
      type: 'checkbox',
      name: 'hide_purpose_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Purpose in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    problemSolved: {
      value: data.problemSolved,
      type: 'tinymce',
      name: 'problemSolved',
      human_name: 'Problem Solved',
      required: false,
      invalid_feedback: null
    },
    hide_problem_solved_in_main_trax: {
      value: data.hide_problem_solved_in_main_trax,
      type: 'checkbox',
      name: 'hide_problem_solved_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Problem Solved in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    why: { value: data.why, type: 'tinymce', name: 'why', human_name: 'Why', required: false, invalid_feedback: null },
    hide_why_in_main_trax: {
      value: data.hide_why_in_main_trax,
      type: 'checkbox',
      name: 'hide_why_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Why in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    promise: { value: data.promise, type: 'tinymce', name: 'promise', human_name: 'Promise', required: false, invalid_feedback: null },
    hide_promise_in_main_trax: {
      value: data.hide_promise_in_main_trax,
      type: 'checkbox',
      name: 'hide_promise_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Promise in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    vision: { value: data.vision, type: 'tinymce', name: 'vision', human_name: 'Vision', required: false, invalid_feedback: null },
    hide_vision_in_main_trax: {
      value: data.hide_vision_in_main_trax,
      type: 'checkbox',
      name: 'hide_vision_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Vision in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    mission: { value: data.mission, type: 'tinymce', name: 'mission', human_name: 'Mission', required: false, invalid_feedback: null },
    hide_mission_in_main_trax: {
      value: data.hide_mission_in_main_trax,
      type: 'checkbox',
      name: 'hide_mission_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Mission in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    coreValues: { value: data.coreValues, type: 'tinymce', name: 'coreValues', human_name: 'Core Values', required: false, invalid_feedback: null },
    hide_core_values_in_main_trax: {
      value: data.hide_core_values_in_main_trax,
      type: 'checkbox',
      name: 'hide_core_values_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Core Values in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    cultureStatement: {
      value: data.cultureStatement,
      type: 'tinymce',
      name: 'cultureStatement',
      human_name: 'Culture Statement',
      required: false,
      invalid_feedback: null
    },
    hide_culture_statement_in_main_trax: {
      value: data.hide_culture_statement_in_main_trax,
      type: 'checkbox',
      name: 'hide_culture_statement_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Culture Statement in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    valueCreated: {
      value: data.valueCreated,
      type: 'tinymce',
      name: 'valueCreated',
      human_name: 'Value Created',
      required: false,
      invalid_feedback: null
    },
    hide_value_created_in_main_trax: {
      value: data.hide_value_created_in_main_trax,
      type: 'checkbox',
      name: 'hide_value_created_in_main_trax',
      human_name: '',
      checkboxLabel: 'Hide Value Created in MainTrax™',
      required: false,
      invalid_feedback: null
    },
    notes: { value: data.notes, type: 'textarea', name: 'notes', human_name: 'Notes', required: false, invalid_feedback: null }
  }

  const orgImageCropSettings = 20 / 9

  const statementOrder = parseOrgMainTraxStatementOrder(data?.main_trax_statement_order)
  const statementGroupedFields = statementOrder.map((key) => [key, ORG_STATEMENT_HIDE_FIELD[key]])
  const groupedFields = [...statementGroupedFields, ['notes']]

  const handleReorderStatementGroups = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return
    if (fromIndex < 0 || toIndex < 0) return
    if (fromIndex >= statementOrder.length || toIndex >= statementOrder.length) return

    const nextOrder = [...statementOrder]
    const [moved] = nextOrder.splice(fromIndex, 1)
    nextOrder.splice(toIndex, 0, moved)
    setFormDatum({
      target: {
        name: 'main_trax_statement_order',
        value: nextOrder,
      },
    })
  }

  // inline styles for the color grid
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '10px'
  }

  const labelStyle = (k: string): React.CSSProperties => {
    const g = gradientFor(k)
    const active = (data?.color_key ?? null) === k
    return {
      backgroundImage: `linear-gradient(90deg, ${g.from}, ${g.to})`,
      color: g.text || '#fff',
      borderRadius: 10,
      padding: '10px 12px',
      fontSize: '.85rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      boxShadow: '0 2px 6px rgba(0,0,0,.08)',
      outline: active ? '2px solid rgba(0,0,0,.28)' : 'none',
      userSelect: 'none'
    }
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="row mt-2 mb-5">
          <div className="col-12 col-md-9">
            <div className="row">
              <OrganizationAdminHeader page_name="Organization Profile" />
            </div>
          </div>

          <div className="col-9">
            <div className="card">
              <div className="card-body">
                <FormOrganizationDetails
                  data={data}
                  customColumns={formParsedValues}
                  colClass="col-12 col-lg-6"
                  statementEditorKeyPrefix={organization_id}
                  readonly={false}
                  setFormDatum={setFormDatum}
                  onSubmit={handleSubmitForm}
                  enableGroupReorder
                  onReorderGroups={handleReorderStatementGroups}
                  groupedFields={groupedFields}
                />
                <small className="text-muted d-block mt-2">
                  Drag the grabber bars on statement headers to change MainTrax™ listing order.
                </small>
              </div>
              <div className="card-footer bg-transparent d-flex flex-row-reverse">
                <button type="submit" form="dataFormDetails" className="btn btn-primary">
                  Save
                </button>
              </div>
            </div>
          </div>

          <div className="col-3">
            {/* Organization Image */}
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-subtitle text-muted">Organization Image</h6>
                {user_organizations_obj.isLoading ? (
                  <Loading />
                ) : (
                  <ImageUploader
                    src={
                      organization.image !== null
                        ? publicUrl(organization.image)
                        : 'https://cepese-polo.info/wp-content/plugins/ldd-directory-lite/public/images/noimage.png'
                    }
                    readonly={false}
                    setTempImgUrl={setTempOrgImg}
                    aspect={orgImageCropSettings}
                  />
                )}
              </div>
            </div>

            {/* Organization Header Color */}
            <div className="card">
              <div className="card-body">
                <h6 className="card-subtitle text-muted mb-2">Organization Header Color</h6>

                <div style={gridStyle}>
                  {/* Default (clear) */}
                  <label style={{ ...labelStyle('zynity'), backgroundImage: 'none', background: '#f2f4f7', color: '#111' }}>
                    <input
                      type="checkbox"
                      checked={(data?.color_key ?? null) === null}
                      onChange={handleClearColor}
                      style={{ width: 16, height: 16, margin: 0 }}
                    />
                    <span>Default</span>
                  </label>

                  {THEME_KEYS.map((k) => (
                    <label key={k} style={labelStyle(k)} title={k}>
                      <input
                        type="checkbox"
                        checked={(data?.color_key ?? null) === k}
                        onChange={() => handlePickColor(k)}
                        style={{ width: 16, height: 16, margin: 0 }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{k}</span>
                    </label>
                  ))}
                </div>

                <small className="text-muted d-block mt-2">
                  Click a color to save instantly. “Default” uses the standard Zynity gradient.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
